import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  IconButton,
  Typography,
} from "@mui/material";
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Cards from '../../ReusableComponents/Cards/Cards';
import CustomTable from "../../ReusableComponents/Table/CustomTable";
import api from '../../api/api';
import { styled } from '@mui/material/styles';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import Calendar from '../../ReusableComponents/Calendar/Calendar';
import en_ja from '../../translations/en_ja';
import bmi_image from '../../../src/assets/bmi_image.png';
import BmiStatus from './BmiStatus';

export default function Dashboard() {

  const [cardsData, setCardsData] = useState([])
  const [clinicData, setClinicData] = useState([])
  const [loading, setLoading] = useState(true);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);

  const open = Boolean(calendarAnchorEl);

  const handleCalendarClick = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const formattedDate = selectedDate
    ? selectedDate.toLocaleDateString('en-CA')
    : new Date().toLocaleDateString('en-CA');

  const ProgressBar = styled('div')(({ value }) => ({
    height: 8,
    width: '100%',
    backgroundColor: '#eee',
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${value}%`,
      backgroundColor: '#ef4444'
    }
  }));

  const columns = [
    { id: 'clinic_name', label: en_ja['Clinic Name'], sortable: true },
    { id: 'clinic_location', label: en_ja['Location'], sortable: true },
    { id: 'phone_number', label: en_ja['Contact'], sortable: true },
    { id: 'available_slots', label: en_ja['Available Slots'], sortable: true },
    {
      id: 'percentage_empty', label: en_ja['Slot Utilization'], sortable: true,
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1} minWidth={120}>
          <ProgressBar value={parseInt(row.percentage_empty)} />
          <span style={{ fontWeight: 400 }}>
            {parseInt(row.percentage_empty)}%
          </span>
        </Box>
      )
    }
  ];

  useEffect(() => {
    getAllClinics();
  }, [formattedDate])

  useEffect(() => {
    getAllCards()
  }, [])

  const getAllClinics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/admin/clinics-with-slots?date=${formattedDate}`);
      if (res) {
        setClinicData(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllCards = () => {
    api.get("/v1/admin/clinic-count")
      .then((res) => {
        setCardsData(res?.data?.data)
      })
      .catch((err) => console.error(err));
  };
  const formatDate = (date) => {
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handleTodayClick = () => {
    setSelectedDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleBmiCardClick = () => {
    setDialogOpen(true);
  }

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 3,
          width: "100%",
        }}
      >
        <Cards
          title={en_ja['Total Clinics']}
          value={cardsData?.totalClinics ?? 0}
          icon={<StoreIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Cards
          title={en_ja['Active Clinics']}
          value={cardsData?.activeClinics ?? 0}
          icon={<CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Cards
          title={en_ja['Inactive Clinics']}
          value={cardsData?.inactiveClinics ?? 0}
          icon={<CancelIcon sx={{ color: "white", fontSize: 24 }} />}
        />
        <Box onClick={handleBmiCardClick} sx={{ cursor: "pointer" }}>
          <Cards
            title={en_ja['High BMI Status']}
            value={cardsData?.bmi_over_25_count ?? 0}
            icon={
              <Box
                component="img"
                src={bmi_image}
                alt="BMI Icon"
                sx={{ width: 32, height: 32, borderRadius: '50%', p: 0.5 }}
              />
            }
          />
        </Box>
      </Box>

      <Typography
        variant="h4"
        fontWeight="700"
        color="#1a1a1a"
        sx={{ my: 2, fontSize: "28px", pt: 2 }}
      >
        {en_ja['Clinic Availability Report']}
      </Typography>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        my: 3,
        borderBottom: '1px solid #e9ecef'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: "22px", letterSpacing: '-0.02em' }}>
            {formatDate(selectedDate)}
          </Typography>
          {!isToday(selectedDate) && (
            <Typography
              variant="body2"
              onClick={handleTodayClick}
              sx={{
                color: '#888',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#dc3545', textDecoration: 'underline' }
              }}
            >
              - {en_ja['Today']}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={handleCalendarClick}
          sx={{
            border: '2px solid #e9ecef',
            backgroundColor: 'white',
            '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#dc3545', transform: 'scale(1.05)' }
          }}
        >
          <CalendarIcon />
        </IconButton>
      </Box>

      <Card sx={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
      }}>
        <CustomTable
          columns={columns}
          rows={clinicData}
          loading={loading}
          showIndexColumn={true} />
      </Card>

      <Calendar
        open={open}
        anchorEl={calendarAnchorEl}
        onClose={handleCalendarClose}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
      />

      <BmiStatus
        dialogOpen={dialogOpen}
        onClose={handleDialogClose}
      />
    </Box>
  )
}
